import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

interface UIState {
  isCartDrawerOpen: boolean;
  isMobileMenuOpen: boolean;
  isSearchOpen: boolean;
  quickEditEnabled: boolean;
  toasts: Toast[];

  openCartDrawer: () => void;
  closeCartDrawer: () => void;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
  openSearch: () => void;
  closeSearch: () => void;
  toggleQuickEdit: () => void;
  addToast: (message: string, type?: Toast['type']) => void;
  removeToast: (id: string) => void;
}

/**
 * Only `quickEditEnabled` is persisted.
 *
 * Everything else here is transient by nature — a cart drawer or a toast that
 * survived a reload would be a bug. Quick Edit is the opposite: it is a working
 * mode a designer turns on and then edits in for an hour, and a page refresh
 * silently switched it off, which reads as the toggle not working.
 *
 * sessionStorage rather than localStorage, so it lasts as long as the tab but
 * does not leave edit affordances switched on the next time someone opens the
 * site — including on a shared machine.
 */
export const useUIStore = create<UIState>()(persist((set) => ({
  isCartDrawerOpen: false,
  isMobileMenuOpen: false,
  isSearchOpen: false,
  quickEditEnabled: false,
  toasts: [],

  openCartDrawer: () => set({ isCartDrawerOpen: true }),
  closeCartDrawer: () => set({ isCartDrawerOpen: false }),
  toggleMobileMenu: () => set((s) => ({ isMobileMenuOpen: !s.isMobileMenuOpen })),
  closeMobileMenu: () => set({ isMobileMenuOpen: false }),
  openSearch: () => set({ isSearchOpen: true }),
  closeSearch: () => set({ isSearchOpen: false }),
  toggleQuickEdit: () => set((s) => ({ quickEditEnabled: !s.quickEditEnabled })),

  addToast: (message, type = 'info') => {
    const id = Math.random().toString(36).slice(2);
    set((s) => ({ toasts: [...s.toasts, { id, message, type }] }));
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, 5000);
  },

  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}), {
  name: 'rumio:ui',
  storage: createJSONStorage(() => sessionStorage),
  // Persist ONLY the working mode. Rehydrating a cart drawer, a mobile menu or
  // a stale toast would each be a visible bug.
  partialize: (state) => ({ quickEditEnabled: state.quickEditEnabled }),
}));
